/**
 * The CARA Source node's inspector.
 *
 * Pick a CARA connection, then say what to read with the same picker the data
 * browser uses (`CaraSearchBuilder`) — so a search that works in the browser
 * works here, and neither drifts from the other. **Test** previews a page using
 * the same endpoint a run uses, and records the resulting columns on the node
 * so every downstream picker knows what arrives even after a reload.
 *
 * Its own file rather than another branch inside `PipelineCanvas.tsx`: that
 * file is already 5,000 lines, and a node's config is exactly the kind of thing
 * that should be readable on its own.
 *
 * **Reads only.** There is no destination here and no CARA target node to wire
 * this into — the platform cannot write to CARA at all (ADR-0023).
 */

import { useEffect, useState } from "react";
import { Button, NumberInput, Stack, Table, Text } from "@mantine/core";
import { IconPlayerPlay } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { CaraSearchBuilder } from "@/features/integration/CaraSearchBuilder";
import { caraSearchReady, caraSelectionFrom } from "@/features/integration/caraSearch";
import { caraSearch, type CaraSearchResult } from "@/shared/api/integration-cara";
import { listConnections } from "@/shared/api/integration";
import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

/** Rows a Test preview shows. The run's own cap is the node's `maxRows`. */
const PREVIEW_ROWS = 50;

function cellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function CaraSourceConfig({
  config,
  editable,
  onChange,
  onColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onColumns?: (cols: string[]) => void;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const search = caraSelectionFrom(config);
  const maxRows = Number(config.maxRows ?? 1000) || 1000;

  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<CaraSearchResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "cara")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        if (!cancelled) toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const onTest = async () => {
    if (!connectionId || !caraSearchReady(search)) return;
    setTesting(true);
    try {
      const token = await getToken();
      const result = await caraSearch(token, connectionId, {
        typeName: search.mode === "builder" ? search.typeName : null,
        body: search.mode === "body" ? search.body : null,
        text: search.text,
        filters: search.filters.filter((f) => f.field),
        fields: search.fields,
        searchInContent: search.searchInContent,
        limit: PREVIEW_ROWS,
      });
      setPreview(result);
      onColumns?.(result.columns); // immediate (this session)
      // Persisted → survives a reload and feeds downstream column pickers
      // without re-running the search.
      onChange("previewColumns", result.columns);
    } catch (err) {
      toastApiError("Test failed", err);
    } finally {
      setTesting(false);
    }
  };

  const columns = preview?.columns ?? [];
  const rows = preview?.rows ?? [];

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="CARA connection"
        placeholder={connections.length ? "Pick a connection" : "No CARA connections set up"}
        data={connections}
        nothingFoundMessage="No CARA connections"
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          // The old selection belongs to the old tenant: a type name from one
          // CARA instance need not exist in another.
          onChange("typeName", "");
          onChange("fields", []);
          onChange("filters", []);
          setPreview(null);
        }}
      />

      {connectionId && (
        <>
          <CaraSearchBuilder
            connectionId={connectionId}
            value={search}
            disabled={!editable}
            onChange={(patch) => {
              for (const [key, value] of Object.entries(patch)) onChange(key, value);
              setPreview(null);
            }}
          />

          <NumberInput
            label="Maximum rows"
            description="The run stops here. CARA views hold six figures of records, so a run that quietly pulled all of them would be a surprise — and every row is held in memory."
            min={1}
            max={20000}
            value={maxRows}
            disabled={!editable}
            onChange={(v) => onChange("maxRows", typeof v === "number" ? v : Number(v) || 1000)}
          />

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => void onTest()}
            loading={testing}
            disabled={!caraSearchReady(search)}
          >
            Test — preview data
          </Button>
        </>
      )}

      {preview && (
        <Stack gap={4} mt="xs">
          <Text size="xs" tt="uppercase" fw={600} c="dimmed">
            Preview
          </Text>
          <Text size="xs" c="dimmed">
            {(preview.total ?? rows.length).toLocaleString()} match(es)
            {preview.truncated ? ` · showing the first ${rows.length}` : ""}
          </Text>
          {rows.length === 0 ? (
            <Text size="xs" c="dimmed">
              No records matched.
            </Text>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table striped withTableBorder verticalSpacing={4} fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    {columns.map((c) => (
                      <Table.Th key={c}>{c}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row, index) => (
                    <Table.Tr key={index}>
                      {columns.map((c) => (
                        <Table.Td key={c}>{cellText(row[c])}</Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Stack>
      )}
    </Stack>
  );
}
